import { logger } from '@/ui'
import { useCampaignUsageRequestMore } from '@jobilla/nexus-sdk'
import { useMemo } from 'react'
import { useJobillaOrgId } from './useJobillaOrgId'

const STORAGE_KEY_PREFIX = 'entitlement-requested:'
const LEGACY_STORAGE_KEY_PREFIX = 'campaign-quota-requested:'

export function useQuotaRequestMore(periodEnd: string | null, requestKey?: string) {
	const organisationId = useJobillaOrgId()

	const resolvedKey = requestKey ?? periodEnd
	const requestAction = requestKey ? 'request_upgrade' : 'request_more_campaigns'
	const storageKey = organisationId && resolvedKey
		? `${STORAGE_KEY_PREFIX}${organisationId}:${resolvedKey}`
		: null
	const legacyStorageKey = organisationId && resolvedKey
		? `${LEGACY_STORAGE_KEY_PREFIX}${organisationId}:${resolvedKey}`
		: null

	const alreadyRequested = useMemo(() => {
		if (!storageKey) { return false }

		const currentValue = localStorage.getItem(storageKey)
		if (currentValue === 'true') {
			return true
		}

		if (!legacyStorageKey) {
			return false
		}

		const legacyValue = localStorage.getItem(legacyStorageKey)
		if (legacyValue !== 'true') {
			return false
		}

		localStorage.setItem(storageKey, 'true')
		return true
	}, [legacyStorageKey, storageKey])

	const mutation = useCampaignUsageRequestMore({
		onSuccess: () => {
			logger.track('Entitlement: requested', {
				category: 'billing',
				data: { type: requestAction, feature: requestKey ?? null },
			})
			if (storageKey) {
				localStorage.setItem(storageKey, 'true')
			}
		},
		onError: (error) => {
			logger.error('Entitlement request failed', {
				tags: { feature: requestKey ?? requestAction, action: requestAction },
				data: { organisation_id: organisationId, error: error.message },
			})
		},
	})

	const hasSent = alreadyRequested || mutation.isSuccess

	const request = () => {
		if (!organisationId) { return }
		mutation.mutate({ organisation: organisationId, data: { type: requestAction, feature: requestKey } })
	}

	return {
		request,
		hasSent,
		isPending: mutation.isPending,
		isError: mutation.isError,
		isDisabled: mutation.isPending || hasSent,
	}
}
